// predict-diagnostic — the PURE half of predict-answer-feedback (Trello wsnTevds).
//
// Predict grading is entirely client-side (`predict` is in RESPONDABLE_TYPES but not
// SERVER_GRADED_TYPES), so nothing on the server can check any of this. Everything here is
// therefore free of DOM and network so it can be unit-tested on the host —
// `test/editor/predict-diagnostic.test.js`, run as
//     npx mocha --opts /dev/null test/editor/predict-diagnostic.test.js
//
// THREE exported jobs, in the order the surface uses them:
//
//   normalizePredictOutput(text, settings)  the grading normalizer
//   gradePrediction(prediction, actual, settings)   pass/fail + WHY it passed
//   diagnosePredictMiss(...) / explainForgivenPass(...)   the two student-facing sentences
//
// ── THE NORMALIZER IS A WRAPPER, NOT A REPLACEMENT ──────────────────────────────────────
//
// The shared `challenge-grading.js` `normalizeOutput` is the ONE normalizer in this repo and
// Code Challenge / debug grade live content against it. It strips only TRAILING blank lines;
// predict's own retired local copy also stripped LEADING ones (`s.replace(/^\n+/, '')`). A
// straight swap would make a prediction that omits a leading blank line start FAILING where
// it passes today — a silent regression on live courses that no automated gate can see. So
// this module calls the shared helper and RE-APPLIES the leading-blank-line strip whenever
// `trailingWhitespace` is on, reproducing today's single-toggle output byte-for-byte.
// (Known, accepted, trivial difference: the shared helper also converts a lone `\r` to `\n`
// and strips `\f`/`\v` at line ends. Neither is reachable from ordinary Python stdout.)
//
// `keepNumericPunctuation` is passed ALWAYS, so predict's "ignore punctuation" keeps `.` and
// `-` significant: without it `3.14` would match `314` on the commonest kind of predict in
// the shipped Year 10 course.
import { normalizeOutput as sharedNormalizeOutput } from './challenge-grading.js';

// The three EXPOSED leniency axes, in the fixed order every sentence uses. `key` is the
// `predict.normalize` boolean; `label` is the HINT-SENTENCE label, which is deliberately
// different from the answer key's (`extra spaces`) and the checkbox's (`Ignore extra
// spaces`). Do not harmonise them.
var AXES = [
  { key: 'caseFold', label: 'capital letters' },
  { key: 'stripPunctuation', label: 'punctuation' },
  { key: 'collapseWhitespace', label: 'spacing' }
];

// The FOURTH, hint-only axis: leading/trailing blank lines and end-of-line spaces. It has no
// checkbox of its own — the existing "Ignore trailing spaces and blank lines" one IS that
// switch — and is a candidate only when a teacher has switched that off, so the most
// invisible possible difference is not the one difference with no hint.
var WHITESPACE_AXIS = 'trailingWhitespace';

// ONE length guard covering the axis probes, the pass note's re-tighten probes AND the
// near-miss edit distance, read from `Math.max(prediction.length, actual.length)`. The
// textarea is capped at MAX_STORED_OUTPUT but grading explicitly uses the untruncated
// value, so a pasted prediction against long machine stdout is otherwise unbounded input.
// Above the bound: no probes, no edit distance, today's generic copy.
export var MAX_DIAGNOSTIC_LENGTH = 2000;

// The near-miss ("spelling") heuristic, reached ONLY when no set of axes explains the miss.
// At most two single-character edits, AND those edits must be a small fraction of the
// output — so `5` against `6` (one edit, one character) is NOT called "very close", which
// would be nonsense advice about spelling on a wrong number.
export var NEAR_MISS_MAX_EDITS = 2;
export var NEAR_MISS_MIN_CHARS_PER_EDIT = 4;

/** The pinned copy. Exported so the spec asserts the strings rather than paraphrasing them. */
export var COPY = {
  whitespace: 'Check for extra spaces or blank lines.',
  missing: 'Part of a line looks like it is missing something — check each line is complete.',
  nearMiss: "That's very close — check your spelling."
};

/**
 * Read an activity's `predict.normalize` into a plain settings object with every key
 * resolved. `trailingWhitespace` is absent-means-ON; the three new ones are
 * absent-means-OFF (the deliberate asymmetry recorded in the model and the route Joi).
 */
export function readSettings(normalize) {
  var n = (normalize && typeof normalize === 'object') ? normalize : {};
  return {
    trailingWhitespace: n.trailingWhitespace !== false,
    caseFold: !!n.caseFold,
    stripPunctuation: !!n.stripPunctuation,
    collapseWhitespace: !!n.collapseWhitespace,
    ignoreAllSpaces: !!n.ignoreAllSpaces
  };
}

/**
 * Normalize one side of the text-mode compare. See the wrapper note at the top of the file:
 * the leading-blank-line strip is re-applied AFTER the shared pipeline, and only when
 * `trailingWhitespace` is on, exactly as predict has always behaved.
 */
export function normalizePredictOutput(text, settings) {
  var s = settings || {};
  var trailing = s.trailingWhitespace !== false;
  var out = sharedNormalizeOutput(text, {
    trailingWhitespace: trailing,
    collapseWhitespace: !!s.collapseWhitespace,
    ignoreAllSpaces: !!s.ignoreAllSpaces,
    caseFold: !!s.caseFold,
    stripPunctuation: !!s.stripPunctuation,
    keepNumericPunctuation: true
  });
  if (trailing) { out = out.replace(/^\n+/, ''); }
  return out;
}

function matchesUnder(prediction, actual, settings) {
  return normalizePredictOutput(prediction, settings) === normalizePredictOutput(actual, settings);
}

/** The activity's settings with the three OPTIONAL relaxations forced off. */
function strictSettings(settings) {
  return {
    trailingWhitespace: settings.trailingWhitespace,
    caseFold: false,
    stripPunctuation: false,
    collapseWhitespace: false,
    ignoreAllSpaces: false
  };
}

function withAxes(settings, axes) {
  var out = {
    trailingWhitespace: settings.trailingWhitespace,
    caseFold: settings.caseFold,
    stripPunctuation: settings.stripPunctuation,
    collapseWhitespace: settings.collapseWhitespace,
    ignoreAllSpaces: settings.ignoreAllSpaces
  };
  for (var i = 0; i < axes.length; i++) {
    if (axes[i] === WHITESPACE_AXIS) { out.trailingWhitespace = true; }
    else { out[axes[i]] = true; }
  }
  return out;
}

/**
 * Grade a text-mode prediction.
 *
 *   strict   — the comparison with the three optional relaxations OFF. This is EXACTLY
 *              today's comparison, so an activity carrying none of the toggles is unchanged.
 *   lenient  — the comparison under the activity's own settings.
 *   correct  — `strict || (lenient && the relaxed prediction is not empty)`.
 *
 * THE RELAXED-TO-EMPTY GUARD APPLIES ONLY TO THE LENIENCY-ASSISTED HALF. With punctuation
 * forgiven a prediction of `"."` relaxes to `''` and would otherwise match a program that
 * printed nothing — but gating the STRICT comparison on it would mark a student wrong on a
 * byte-identical answer (predicting `...` for a program that really prints `...`), which is
 * the precise stuck-student failure this card exists to fix.
 */
export function gradePrediction(prediction, actual, settings) {
  var s = readSettings(settings);
  var strict = matchesUnder(prediction, actual, strictSettings(s));
  var lenient = matchesUnder(prediction, actual, s);
  var relaxedEmpty = normalizePredictOutput(prediction, s) === '';
  var correct = strict || (lenient && !relaxedEmpty);
  return {
    correct: correct,
    strict: strict,
    lenient: lenient,
    relaxedEmpty: relaxedEmpty,
    // The pass happened ONLY because a teacher forgave something.
    leniencyAssisted: correct && !strict
  };
}

/**
 * The five suppression states, ANY ONE of which is sufficient. In every case the miss keeps
 * today's generic copy and a leniency-assisted pass keeps today's success copy — guessing
 * otherwise ships confidently wrong advice to a child.
 *
 *   graphics    a text-mode pgzero/pygame predict's stdout is the SDL boot banner
 *   errored     `actual` is a partial stdout; the traceback lives only in the notes
 *   inputAsked  the run parked mid-way, so `actual` is a PARTIAL stdout
 *   over-length the shared length guard tripped
 *   relaxed-to-empty  there is nothing left of the prediction to make a claim about
 *
 * The list governs the forgiven-pass note EXACTLY as it governs the miss diagnostic: the
 * note's own last sentence is "Compare yours with the real output", and in these states the
 * actual-output block holds stdout PLUS the traceback or the input-parked prose.
 */
function suppressed(prediction, actual, settings, runState) {
  var r = runState || {};
  if (r.graphics || r.errored || r.inputAsked) { return true; }
  var p = String(prediction == null ? '' : prediction);
  var a = String(actual == null ? '' : actual);
  if (Math.max(p.length, a.length) > MAX_DIAGNOSTIC_LENGTH) { return true; }
  if (normalizePredictOutput(p, readSettings(settings)) === '') { return true; }
  return false;
}

/** Every combination of `items` of exactly `size`, in ascending index order. */
function combinations(items, size) {
  var out = [];
  (function walk(start, acc) {
    if (acc.length === size) { out.push(acc.slice()); return; }
    for (var i = start; i < items.length; i++) {
      acc.push(items[i]);
      walk(i + 1, acc);
      acc.pop();
    }
  })(0, []);
  return out;
}

/**
 * The smallest set of relaxations whose JOINT application converts miss -> match.
 *
 * Candidates are the exposed axes the teacher has NOT already switched on — naming an axis
 * the activity just ignored would be actively misleading — plus the hint-only whitespace
 * axis when `trailingWhitespace` is off.
 *
 * SIZES ASCENDING ACROSS ALL FOUR CANDIDATES, never one axis at a time and never capped at
 * three. A prediction differing in BOTH capitals and punctuation is fixed by neither single
 * probe, and one differing in both capitals and a leading blank line is fixed by no
 * three-exposed-axis set — either would fall through to the near-miss branch and tell a
 * student to check their SPELLING when the spelling was fine, which is the exact misleading
 * outcome this search exists to prevent.
 *
 * Ties: the FIRST matching subset at the smallest size wins, with candidates in the fixed
 * order above. The only realistic tie is `collapseWhitespace` against the whitespace axis
 * (collapse also trims each line's ends), and the ledger records that as a cosmetic choice
 * with no wrong reading because both candidate sentences are true of the difference.
 *
 * Returns an array of axis keys, or `null` when nothing explains the miss.
 */
export function smallestExplainingSet(prediction, actual, settings) {
  var s = readSettings(settings);
  var candidates = [];
  for (var i = 0; i < AXES.length; i++) {
    if (!s[AXES[i].key]) { candidates.push(AXES[i].key); }
  }
  if (!s.trailingWhitespace) { candidates.push(WHITESPACE_AXIS); }

  for (var size = 1; size <= candidates.length; size++) {
    var sets = combinations(candidates, size);
    for (var j = 0; j < sets.length; j++) {
      if (matchesUnder(prediction, actual, withAxes(s, sets[j]))) { return sets[j]; }
    }
  }
  return null;
}

/**
 * Levenshtein distance, BOUNDED at `max` (returns `max + 1` for anything further apart).
 * Banded to O(n·max) rather than O(n·m) so the near-miss test can never stall the surface
 * even at the top of the length guard.
 */
export function boundedEditDistance(a, b, max) {
  if (a === b) { return 0; }
  var n = a.length, m = b.length;
  if (Math.abs(n - m) > max) { return max + 1; }
  // An EMPTY `b` must short-circuit. The band below is `lo = max(1, i - max)` to
  // `hi = min(m, i + max)`; with `m === 0` that is `lo >= 1 > hi === 0`, so the inner loop
  // never runs, `best` keeps its `max + 1` sentinel and row 1 returns `max + 1` for a pair
  // whose real distance is just `n`. The reverse case needs no guard: `n === 0` skips the
  // outer loop and returns the already-correct `prev[m] === m`.
  if (m === 0) { return n > max ? max + 1 : n; }
  var prev = new Array(m + 1);
  var curr = new Array(m + 1);
  for (var j = 0; j <= m; j++) { prev[j] = j; }
  for (var i = 1; i <= n; i++) {
    var lo = Math.max(1, i - max);
    var hi = Math.min(m, i + max);
    // The band moves by one row at a time, so the only cells read outside it are these two
    // sentinels and the ones the previous row wrote.
    curr[lo - 1] = (lo - 1 === 0) ? i : max + 1;
    var best = max + 1;
    for (var k = lo; k <= hi; k++) {
      var cost = a.charAt(i - 1) === b.charAt(k - 1) ? 0 : 1;
      var v = Math.min(prev[k] + 1, curr[k - 1] + 1, prev[k - 1] + cost);
      if (v > max) { v = max + 1; }
      curr[k] = v;
      if (v < best) { best = v; }
    }
    if (hi < m) { curr[hi + 1] = max + 1; }
    if (best > max) { return max + 1; }
    var swap = prev; prev = curr; curr = swap;
  }
  return prev[m] > max ? max + 1 : prev[m];
}

/**
 * The near-miss test. Runs on the forms relaxed by all three EXPOSED axes (so a difference
 * the axis search already rejected as unexplaining is not re-counted as a typo), behind a
 * cheap length prefilter, and only inside the shared length guard.
 */
export function isNearMiss(prediction, actual, settings) {
  var s = readSettings(settings);
  var relaxed = withAxes(s, ['caseFold', 'stripPunctuation', 'collapseWhitespace']);
  var p = normalizePredictOutput(prediction, relaxed);
  var a = normalizePredictOutput(actual, relaxed);
  if (p === a) { return false; }
  if (Math.abs(p.length - a.length) > NEAR_MISS_MAX_EDITS) { return false; }
  var longer = Math.max(p.length, a.length);
  var dist = boundedEditDistance(p, a, NEAR_MISS_MAX_EDITS);
  if (dist < 1 || dist > NEAR_MISS_MAX_EDITS) { return false; }
  return dist * NEAR_MISS_MIN_CHARS_PER_EDIT <= longer;
}

/**
 * Is the prediction a line-by-line truncation of the real output — every differing line is
 * the real line with something cut off its start or end (`110` for `0b110`)? That is
 * "missing something", not a typo, so it is checked before the spelling near-miss.
 */
export function isMissingPart(prediction, actual, settings) {
  var s = readSettings(settings);
  var relaxed = withAxes(s, ['caseFold', 'stripPunctuation', 'collapseWhitespace']);
  var p = normalizePredictOutput(prediction, relaxed);
  var a = normalizePredictOutput(actual, relaxed);
  if (p === a) { return false; }
  var pl = p.split('\n');
  var al = a.split('\n');
  if (pl.length !== al.length) { return false; }
  var differing = 0;
  for (var i = 0; i < pl.length; i++) {
    if (pl[i] === al[i]) { continue; }
    differing++;
    var short = pl[i];
    var full = al[i];
    if (short.length === 0 || short.length >= full.length) { return false; }
    if (full.indexOf(short) !== 0 && full.lastIndexOf(short) !== full.length - short.length) { return false; }
  }
  return differing > 0;
}

/** Join labels as `a`, `a and b`, `a, b and c`. */
function joinLabels(labels) {
  if (labels.length === 1) { return labels[0]; }
  return labels.slice(0, -1).join(', ') + ' and ' + labels[labels.length - 1];
}

function labelsFor(axisKeys) {
  var out = [];
  for (var i = 0; i < AXES.length; i++) {
    if (axisKeys.indexOf(AXES[i].key) !== -1) { out.push(AXES[i].label); }
  }
  return out;
}

/**
 * The wrong-answer diagnostic: what KIND of thing was wrong, never the real output, the
 * differing characters or a line number. Returns the pinned sentence, or `null` for "no
 * diagnostic", in which case the caller leaves today's generic coaching sentence standing.
 *
 * RENDERING RULE: when the hint-only whitespace axis is in the winning set, its standalone
 * sentence is emitted ALONE and its co-winners go unmentioned. That is a rendering rule, not
 * a search-scope rule — the axis still participates fully in the search above, because
 * partial advice beats the wrong advice a narrower search would produce.
 */
export function diagnosePredictMiss(prediction, actual, settings, runState) {
  if (suppressed(prediction, actual, settings, runState)) { return null; }
  var set = smallestExplainingSet(prediction, actual, settings);
  if (set) {
    if (set.indexOf(WHITESPACE_AXIS) !== -1) { return COPY.whitespace; }
    return 'Check your ' + joinLabels(labelsFor(set)) + '.';
  }
  if (isMissingPart(prediction, actual, settings)) { return COPY.missing; }
  if (isNearMiss(prediction, actual, settings)) { return COPY.nearMiss; }
  return null;
}

/**
 * The forgiven-pass note: a student who passed ONLY because a teacher forgave something is
 * told so, quietly.
 *
 * It names the MINIMAL necessary set — each forgiven axis is re-tightened alone and named
 * only if the match then breaks. Listing every enabled toggle would tell a student they made
 * a punctuation error they did not make, which defeats the note's whole purpose.
 *
 * `trailingWhitespace` is NEVER a forgiven axis here. It is on by default, so counting it
 * would make every existing predict in every course start disclosing on a stray trailing
 * newline — a visible change on live content where no teacher enabled anything.
 *
 * Returns the pinned sentence, or `null`.
 */
export function explainForgivenPass(prediction, actual, settings, runState) {
  var s = readSettings(settings);
  var graded = gradePrediction(prediction, actual, s);
  if (!graded.leniencyAssisted) { return null; }
  if (suppressed(prediction, actual, s, runState)) { return null; }

  var loadBearing = [];
  for (var i = 0; i < AXES.length; i++) {
    var key = AXES[i].key;
    if (!s[key]) { continue; }
    var tightened = withAxes(s, []);
    tightened[key] = false;
    if (!matchesUnder(prediction, actual, tightened)) { loadBearing.push(key); }
  }
  // Degenerate and not reachable from any real pair of strings (case-folding cannot fix a
  // punctuation difference and vice versa), but if no single axis is individually
  // load-bearing there is no honest claim to make, so make none.
  if (!loadBearing.length) { return null; }

  return 'Not quite identical — this activity isn\'t checking ' + joinLabels(labelsFor(loadBearing))
    + '. Compare yours with the real output.';
}

/**
 * Where two strings differ, as segment lists ready to render.
 *
 * ONLY EVER CALLED ONCE THE ANSWER IS ALREADY ON SCREEN — a leniency-assisted pass or a
 * give-up. It must NEVER be reached from the miss path: highlighting the differing characters
 * of a withheld answer IS showing the answer, and the spec requirement "The real output is
 * withheld until the answer is earned or requested" would fall.
 *
 * Compared LINE BY LINE, positionally, then by common prefix and suffix within each line.
 * That is the right model for this surface — a predict answer is a handful of short lines the
 * student typed to mirror the program's output — and it keeps the cost linear where a general
 * LCS diff would be O(n·m) on a string the length guard only bounds at 2000.
 *
 * Returns `null` when there is nothing honest to show: a suppressed run (see `suppressed`),
 * or two strings that are already identical.
 *
 * @returns {{yours: Array, actual: Array, differences: Number}|null}
 *          each side a list of `{ text, differs }`, in order, newlines included as their own
 *          non-differing segments so a renderer can emit them verbatim.
 */
export function comparePrediction(prediction, actual, settings, runState) {
  var p = String(prediction == null ? '' : prediction);
  var a = String(actual == null ? '' : actual);
  if (p === a) { return null; }
  if (suppressed(p, a, readSettings(settings), runState)) { return null; }

  var pl = p.split('\n');
  var al = a.split('\n');
  var yours = [], theirs = [], differences = 0;

  for (var i = 0; i < Math.max(pl.length, al.length); i++) {
    if (i > 0) { yours.push({ text: '\n', differs: false }); theirs.push({ text: '\n', differs: false }); }
    var y = pl[i], t = al[i];

    // A line one side simply does not have. Marking the whole of the other side is the honest
    // reading: every character of it is a difference.
    if (y === undefined) { if (t !== '') { theirs.push({ text: t, differs: true }); differences++; } continue; }
    if (t === undefined) { if (y !== '') { yours.push({ text: y, differs: true }); differences++; } continue; }
    if (y === t) { if (y !== '') { yours.push({ text: y, differs: false }); theirs.push({ text: t, differs: false }); } continue; }

    // Common prefix, then common suffix from what is left. The two spans are clamped so they
    // cannot overlap on a pair like `aa` / `aaa`, which would otherwise double-count.
    var pre = 0;
    while (pre < y.length && pre < t.length && y.charAt(pre) === t.charAt(pre)) { pre++; }
    var suf = 0;
    while (suf < y.length - pre && suf < t.length - pre
           && y.charAt(y.length - 1 - suf) === t.charAt(t.length - 1 - suf)) { suf++; }

    push(yours, y, pre, suf);
    push(theirs, t, pre, suf);
    differences++;
  }

  return differences ? { yours: merge(yours), actual: merge(theirs), differences: differences } : null;

  function push(into, line, pre, suf) {
    var mid = line.slice(pre, line.length - suf);
    if (pre) { into.push({ text: line.slice(0, pre), differs: false }); }
    if (mid) { into.push({ text: mid, differs: true }); }
    if (suf) { into.push({ text: line.slice(line.length - suf), differs: false }); }
  }

  /** Fold neighbouring segments that share a flag, so a renderer emits one span not twenty. */
  function merge(segs) {
    var out = [];
    for (var i = 0; i < segs.length; i++) {
      var last = out[out.length - 1];
      if (last && last.differs === segs[i].differs) { last.text += segs[i].text; }
      else { out.push({ text: segs[i].text, differs: segs[i].differs }); }
    }
    return out;
  }
}
